import { DestroyRef, Directive, ElementRef, HostListener, Input, OnChanges, Renderer2 } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { API, MediumDTO, ThumbnailSize } from '../services/api/api';
import { MediumViewerComponent } from '../components/controls/medium-viewer/medium-viewer.component';
import { UtilService } from '../services/util.service';
import { AppEnvironment } from '../app.environment';
import { switchMap, take, takeWhile } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';

@Directive({
  selector: '[sbMedium]',
  standalone: false,
})
export class SbMediumDirective implements OnChanges {
  static FILE_IMG: HTMLImageElement;

  supportedCodecs: string[] = ['av1', 'avc', 'h264', 'h263', 'mp4ves', 'mpeg1', 'mpeg2', 'theora', 'vp8', 'vp9'];

  @Input() still: boolean;
  @Input() noCache: boolean;
  @Input() noPreview: boolean;

  // tslint:disable-next-line:variable-name
  _medium: MediumDTO | null;
  // tslint:disable-next-line:variable-name
  _thumbnail: ThumbnailSize;
  isMouseOver = false;

  constructor(
    private readonly modal: NgbModal,
    private readonly api: API,
    private readonly _elRef: ElementRef,
    private readonly _renderer: Renderer2,
    private readonly util: UtilService,
    private readonly destroyRef: DestroyRef
  ) {
    this.still = true;
    this.noCache = false;
    this.noPreview = false;

    // tslint:disable-next-line:no-any

    this._medium = null;
    this._thumbnail = null;
    this.isMouseOver = false;
  }

  @Input('sbMedium') set medium(medium: MediumDTO | null) {
    this._medium = medium;
  }

  get medium(): MediumDTO | null {
    return this._medium;
  }

  @Input() set thumbnail(thumbnail: ThumbnailSize) {
    this._thumbnail = thumbnail;
  }

  get thumbnail(): ThumbnailSize {
    return this._thumbnail;
  }

  @HostListener('click', ['$event'])
  onClick() {
    this.onDoubleClick();
  }

  @HostListener('mouseover', ['$event'])
  onMouseOver() {
    this.isMouseOver = true;
  }

  @HostListener('mouseout', ['$event'])
  onMouseOut() {
    this.isMouseOver = false;
  }

  ngOnChanges() {
    if (!SbMediumDirective.FILE_IMG) {
      this.loadFileImg();
    } else {
      this.setSrc();
    }
  }

  onDoubleClick() {
    if (!this.noPreview) {
      if (this.medium?.id && this.thumbnail) {
        const modal = this.modal.open(MediumViewerComponent, { size: 'lg' });
        (modal.componentInstance as MediumViewerComponent).medium = this.medium;
        (modal.componentInstance as MediumViewerComponent).modalRef = modal;
      }
    }
  }

  setSrc(t: Date | null = null) {
    const forObject = this._elRef.nativeElement.tagName.toLowerCase();
    let urlProperty = null;
    switch (forObject) {
      case 'object':
        urlProperty = 'data';
        break;
      case 'video':
      case 'img':
      case 'audio':
        urlProperty = 'src';
        break;
    }
    if (urlProperty) {
      __ngRendererSetElementAttributeHelper(this._renderer, this._elRef.nativeElement, urlProperty, this.mediaUrl(t));
    } else {
      this.api
        .GET(this.mediaUrl(t))
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe(data => {
          if (forObject === 'input') {
            // tslint:disable-next-line:no-any
            __ngRendererSetElementAttributeHelper(this._renderer, this._elRef.nativeElement, 'value', data);
          } else {
            // tslint:disable-next-line:no-any
            this._elRef.nativeElement.innerHTML = data;
          }
        });
    }
    if (forObject === 'object') {
      __ngRendererSetElementAttributeHelper(this._renderer, this._elRef.nativeElement, 'type', 'application/' + this.medium?.contentType);
    }
    __ngRendererSetElementAttributeHelper(this._renderer, this._elRef.nativeElement, 'title', this.mediaInfos());
    if (this.medium?.processing) {
      this.checkProcessingProgress();
    }
  }

  checkProcessingProgress() {
    if (this.medium?.processing) {
      interval(2500)
        .pipe(
          switchMap(() => this.api.mm_Medium_retrieveById(this.medium.id, { meta: true })),
          takeWhile(medium => medium?.processing, true),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(data => {
          this.medium = data;
          if (!this.medium?.processing) {
            this.setSrc(new Date());
          }
        });
    } else {
      this.setSrc(new Date());
    }
  }

  loadFileImg() {
    const that = this;
    const img = new Image();
    img.onload = () => {
      SbMediumDirective.FILE_IMG = img;
      that.setSrc();
    };
    img.src = '/assets/img/base_file.svg';
  }

  mediaUrl(t: Date | null = null) {
    if (!this.medium?.id) {
      return '/assets/img/missing_file.svg';
    }
    if (this.medium.processing) {
      if (!this.medium.size || this.medium.size === 0) {
        return '/assets/img/missing_file.svg';
      }
      return '/assets/img/processing_file.svg';
    }

    let imageToUse: string;
    if (this.still) {
      imageToUse = this.medium?.stillImage ? (this.medium.stillImage as unknown as string) : null;
    } else {
      imageToUse = this.medium?.url ? (this.medium.url as unknown as string) : null;
    }

    if (this.thumbnail && !this.medium.contentType) {
      const canvas = document.createElement('canvas');
      canvas.width = 250;
      canvas.height = 250;
      const context = canvas.getContext('2d');
      if (context) {
        context.drawImage(SbMediumDirective.FILE_IMG, 25, 50, 200, 200);
        // Fill Text
        context.lineWidth = 1;
        context.fillStyle = '#000';
        context.font = '36px sans-serif';
        context.textAlign = 'center';

        if (this.medium.format) {
          context.fillText(this.medium.format.id, 125, 50);
        }
      }
      return canvas.toDataURL();
    } else if (imageToUse) {
      if (AppEnvironment.SCREENSHOT_MODE) {
        return AppEnvironment.EXAMPLE_IMAGE(this.medium.name);
      }
      let url = imageToUse;
      if (this.thumbnail) {
        url = this.util.appendQueryParamToUrl(url, 'thumbnail=' + this.thumbnail);
      }
      if (t) {
        url = this.util.appendQueryParamToUrl(url, 't=' + t.getTime());
      }
      if (this.noCache) {
        const nD = new Date();
        url = this.util.appendQueryParamToUrl(url, 't=' + nD.getTime());
      }
      if (this.medium.contentType.indexOf('video') !== -1 && !this.thumbnail) {
        const codecString = this.medium?.format?.videoCodec;
        let addThumbnail: boolean = true;
        if (codecString) {
          const format = codecString.split(' ').map(codec => codec.toLowerCase());
          if (format.length === 2) {
            addThumbnail = !(this.supportedCodecs.includes(format[0]) || this.supportedCodecs.includes(format[1]));
          }
        }
        if (addThumbnail) {
          url = this.util.appendQueryParamToUrl(url, 'thumbnail=XL');
        }
      }
      return url;
    } else {
      let url = '/core/StreamResource?commercial=' + this.medium.id;
      if (this.thumbnail) {
        url += '&thumbnail=' + this.thumbnail;
      }
      const tw = new Date();
      url += '&' + 't=' + tw.getTime();
      return url;
    }
  }

  mediaInfos() {
    let ret = '';
    if (this.medium?.name) {
      ret += '\nName: ' + this.medium.name;
    }
    if (this.medium?.resolution) {
      ret += '\nAuflösung: ' + this.medium.resolution.width + 'x' + this.medium.resolution.height + 'px';
    }
    if (this.medium?.duration) {
      ret += '\nDauer: ' + ((this.medium.duration * 10.0) / 10.0).toFixed(1) + ' Sek.';
    }
    if (this.medium?.format?.videoCodec) {
      ret += '\nVideo Codec: ' + this.medium.format.videoCodec;
    }
    if (this.medium?.format?.audioCodec) {
      ret += '\nAudio Codec: ' + this.medium.format.audioCodec;
    }
    if (this.medium?.size) {
      let size = this.medium.size;
      let unit: string = 'Byte';
      if (size > 1024) {
        size = size / 1024;
        unit = 'KB';
      }
      if (size > 1024) {
        size = size / 1024;
        unit = 'MB';
      }
      const plural = size === 1 ? '' : 's';
      ret += '\nGröße: ' + size.toFixed(unit !== 'Byte' ? 1 : 0) + ' ' + unit + plural;
    }
    if (this.medium?.id) {
      ret += '\nMedium ID: ' + this.medium.id;
    }
    return ret.length > 0 ? ret.substr(1) : ret;
  }
}

type AnyDuringRendererMigration = any;

function __ngRendererSplitNamespaceHelper(name: AnyDuringRendererMigration) {
  if (name[0] === ':') {
    const match = name.match(/^:([^:]+):(.+)$/);
    return [match[1], match[2]];
  }
  return ['', name];
}

function __ngRendererSetElementAttributeHelper(
  renderer: AnyDuringRendererMigration,
  element: AnyDuringRendererMigration,
  namespaceAndName: AnyDuringRendererMigration,
  value?: AnyDuringRendererMigration
) {
  const [namespace, name] = __ngRendererSplitNamespaceHelper(namespaceAndName);
  if (value) {
    renderer.setAttribute(element, name, value, namespace);
  } else {
    renderer.removeAttribute(element, name, namespace);
  }
}
